import { createSettings, type Settings } from "../../domain/settings/model/settings";
import type { SettingsRepository } from "../../domain/settings/settings-repository";
import { persistedSettingsSchema } from "./settings-schema";

/** `Settings`の保存先キー（`spec/plan.md` 計画8実施メモと一致させること）。 */
const SETTINGS_KEY = "vimquest.settings.v1";

function toPersisted(settings: Settings) {
  return { schemaVersion: 1 as const, ...settings };
}

/**
 * localStorageを保存先とする`SettingsRepository`の実装。
 * 壊れたJSON・Zod検証失敗のいずれも例外を投げず、既定の`Settings`を返す
 * （`Progress`と異なり`.corrupt`退避は行わない — 失っても既定値に戻るだけのため）。
 */
export class LocalStorageSettingsRepository implements SettingsRepository {
  constructor(private readonly storage: Storage = window.localStorage) {}

  load(): Settings {
    const raw = this.storage.getItem(SETTINGS_KEY);
    if (raw === null) {
      return createSettings();
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return createSettings();
    }

    const result = persistedSettingsSchema.safeParse(parsed);
    if (!result.success) {
      return createSettings();
    }

    const { schemaVersion: _schemaVersion, ...settings } = result.data;
    return settings;
  }

  save(settings: Settings): void {
    try {
      this.storage.setItem(SETTINGS_KEY, JSON.stringify(toPersisted(settings)));
    } catch {
      // 容量超過等でsetItemが例外を投げても、設定操作自体は止めない（ベストエフォート）。
    }
  }
}
